/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import type { IMessage } from 'types/copilot';
import type { ProjectBase } from 'types/projects';
import logger from './logger';

/**
 * The Copilot conversations, kept across reloads and new tabs.
 *
 * `localStorage` rather than `sessionStorage` is the whole point: the session
 * storage the session id used to live in is scoped to a single tab, so opening
 * Copilot in a second tab, or restoring a closed one, started from nothing.
 *
 * Each record holds a transcript and its session id together on purpose. The
 * transcript is what the user sees and the session id is what the Copilot
 * endpoint keys its server side memory on (sent as `x-request-id`), so they are
 * two halves of one conversation — restoring one without the other would show a
 * conversation the server has forgotten, or continue a server session whose
 * transcript is gone.
 *
 * There is one record per user and organization, under a key naming both.
 * `localStorage` outlives the sign-in, so a record not keyed on the user would
 * be handed to whoever signs in on this browser next. And tabs share
 * `localStorage`, so a record not keyed on the organization would have tabs
 * open on different organizations taking each other's conversation off the
 * screen, or sending one organization's questions into the other's server side
 * session.
 */
export const COPILOT_CHAT_KEY_PREFIX = 'devant_copilot_chat:';

/**
 * How long a conversation outlives the tab it was held in. Measured from the
 * last activity, so a chat that is still being used never expires under the
 * user; one left alone for a week is stale enough to start over.
 */
export const COPILOT_CHAT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Transcripts carry whole tool results (API Chat responses, Integration Builder
 * payloads), so a long conversation can grow past what `localStorage` will take
 * — the quota is around 5MB for the whole origin, shared with everything else
 * the console keeps there. Past this the conversation is no longer stored at
 * all rather than risking a write that throws and evicts nothing.
 */
const MAX_SERIALIZED_LENGTH = 2_000_000;

/**
 * How a write went.
 *
 * - `stored`: the record now holds what was written.
 * - `too-large`: the record was over `MAX_SERIALIZED_LENGTH`, and has been
 *   removed rather than left holding an older transcript. The conversation can
 *   only carry on in memory, and will not be restored after a reload.
 * - `failed`: `localStorage` refused it — out of quota, or blocked. Whatever
 *   was stored before is still there.
 * - `gone`: the record had been removed, by another tab starting a new chat or
 *   signing out, and was not written back.
 */
export type CopilotChatWrite = 'stored' | 'too-large' | 'failed' | 'gone';

export interface PersistedCopilotChat {
  /** Session id the Copilot endpoint keys its server side memory on. */
  sessionId: string | null;
  messages: IMessage[];
  /**
   * Project chosen from within the chat for an Integration Builder action. It
   * is conversation scoped state, so it is restored with the conversation.
   */
  selectedProject: ProjectBase | null;
  /** Epoch ms of the last write. The record expires `COPILOT_CHAT_TTL_MS` later. */
  updatedAt: number;
}

/**
 * The key the conversation of `userId` in `orgUuid` is stored under, or null
 * until both are known. With either missing there is no record to read or
 * write: a key short of the user would be shared by everyone who signs in on
 * this browser, and one short of the organization by every organization.
 */
export const getCopilotChatKey = (
  userId: string | undefined,
  orgUuid: string | undefined
): string | null =>
  userId && orgUuid
    ? `${COPILOT_CHAT_KEY_PREFIX}${encodeURIComponent(
        userId
      )}:${encodeURIComponent(orgUuid)}`
    : null;

/**
 * Keys of every stored Copilot conversation, whoever and whichever
 * organization it belongs to. Collected up front, since removing items while
 * walking `localStorage` shifts the indices.
 */
const listCopilotChatKeys = (): string[] => {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(COPILOT_CHAT_KEY_PREFIX)) {
        keys.push(key);
      }
    }
  } catch (error) {
    logger.error('Failed to list the stored Copilot chats', error);
  }
  return keys;
};

/**
 * Drops one conversation — transcript, session id and all. This is what
 * starting a new chat wants.
 *
 * Every access to `localStorage` is guarded: it throws outright in Safari
 * private mode and wherever the browser is set to block site data, and losing a
 * conversation must never break the console.
 */
export const clearCopilotChat = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logger.error('Failed to clear the stored Copilot chat', error);
  }
};

/**
 * Drops every stored conversation, for every user and organization. This is
 * what signing out wants: none of it should outlive the sign-in on a browser
 * someone else may use next.
 */
export const clearAllCopilotChats = () => {
  listCopilotChatKeys().forEach(clearCopilotChat);
};

/**
 * The conversation stored under `key`, or null when there is none, it cannot be
 * read, or it has gone stale. An expired record is removed on the way out.
 */
export const readCopilotChat = (key: string): PersistedCopilotChat | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch (error) {
    logger.error('Failed to read the stored Copilot chat', error);
    return null;
  }
  if (!raw) {
    return null;
  }

  let stored: Partial<PersistedCopilotChat> | null = null;
  try {
    stored = JSON.parse(raw);
  } catch (error) {
    // Written by an older version of this format, or truncated by a write that
    // ran out of quota. Either way it is not worth keeping.
    logger.error('Failed to parse the stored Copilot chat', error);
    clearCopilotChat(key);
    return null;
  }

  if (!stored || !Array.isArray(stored.messages)) {
    clearCopilotChat(key);
    return null;
  }

  const updatedAt = Number(stored.updatedAt);
  if (
    !Number.isFinite(updatedAt) ||
    Date.now() - updatedAt > COPILOT_CHAT_TTL_MS
  ) {
    clearCopilotChat(key);
    return null;
  }

  return {
    sessionId: stored.sessionId ?? null,
    messages: stored.messages,
    selectedProject: stored.selectedProject ?? null,
    updatedAt,
  };
};

/**
 * Removes every stored conversation that has expired or cannot be read. A
 * record is otherwise only checked when its own user and organization come
 * back to it, so one left by a user or an organization that never does would
 * sit on the origin's quota indefinitely.
 */
export const pruneCopilotChats = () => {
  listCopilotChatKeys().forEach((key) => readCopilotChat(key));
};

/**
 * Writes the record, stamped with the time of the write.
 *
 * Both writers — the provider for the transcript and the request layer for the
 * session id — go through a read, merge and write, so neither drops the other's
 * half of the conversation.
 */
const writeCopilotChat = (
  key: string,
  chat: Omit<PersistedCopilotChat, 'updatedAt'>
): CopilotChatWrite => {
  const serialized = JSON.stringify({ ...chat, updatedAt: Date.now() });
  if (serialized.length > MAX_SERIALIZED_LENGTH) {
    // Too big to keep. The record goes rather than being left behind with an
    // older transcript: a reload would restore a conversation missing its last
    // turns, and a new tab would pick up its session id and send questions into
    // a conversation it cannot show.
    logger.warn(
      'Copilot chat is too large to store, it will not be restored after a reload'
    );
    clearCopilotChat(key);
    return 'too-large';
  }

  try {
    localStorage.setItem(key, serialized);
  } catch (error) {
    logger.error('Failed to store the Copilot chat', error);
    return 'failed';
  }

  return 'stored';
};

/**
 * Stores the conversation under `key`, keeping the session id already on
 * record so the restored transcript stays tied to the server side session.
 * The session id is handed back too, for a conversation that turns out to be
 * too large to store and has to carry on without its record.
 *
 * Only a tab holding changes the record does not have calls this — see
 * `decideCopilotChatSync` for why that is what keeps tabs from overwriting one
 * another.
 *
 * `onlyIfStored` is for a tab whose conversation is already on record: if the
 * record has gone, another tab ended the conversation, and writing it back
 * would bring it back to life in every tab.
 */
export const persistCopilotChat = (
  key: string,
  messages: IMessage[],
  selectedProject: ProjectBase | null,
  onlyIfStored: boolean
): { result: CopilotChatWrite; sessionId: string | null } => {
  const stored = readCopilotChat(key);
  const sessionId = stored?.sessionId ?? null;
  if (!stored && onlyIfStored) {
    return { result: 'gone', sessionId };
  }
  return {
    result: writeCopilotChat(key, { sessionId, messages, selectedProject }),
    sessionId,
  };
};

/** What a tab does when its stored conversation changes under it. */
export type CopilotChatSync = 'adopt' | 'clear' | 'ignore';

/**
 * Tabs on the same user and organization share one record and one session id,
 * so they are all looking at the same conversation, and the one holding it in
 * full is whichever tab is answering. That tab is the only one with changes of
 * its own — Copilot streams into the tab the question was asked from — so
 * `hasLocalChanges` is enough to sort out who writes and who follows, with no
 * need to diff two transcripts.
 *
 * A transcript cannot be diffed usefully anyway: an answer arrives by filling
 * in the message that was already appended for it, so the conversation a tab is
 * midway through answering has the same messages as the one another tab is
 * looking at, and only the contents differ.
 *
 * Only a change to the tab's own key is decided here, so a record belonging to
 * another user or organization never has to be told apart.
 *
 * - `clear`: the record is gone — another tab started a new chat or signed out,
 *   and the session id went with it, so this conversation is over too. This
 *   holds even for the tab that is answering; there is nothing left to answer
 *   into.
 * - `ignore`: this tab has changes of its own and is the one others should be
 *   following.
 * - `adopt`: take the record as it stands.
 */
export const decideCopilotChatSync = (
  incoming: PersistedCopilotChat | null,
  hasLocalChanges: boolean
): CopilotChatSync => {
  if (!incoming) {
    return 'clear';
  }
  return hasLocalChanges ? 'ignore' : 'adopt';
};

/** The session id of the conversation under `key`, or null when there is none. */
export const getCopilotSessionId = (key: string): string | null =>
  readCopilotChat(key)?.sessionId ?? null;

/**
 * Stores the session id against the conversation under `key`, keeping whatever
 * transcript is already on record.
 */
export const setCopilotSessionId = (
  key: string,
  sessionId: string
): CopilotChatWrite => {
  const stored = readCopilotChat(key);
  return writeCopilotChat(key, {
    sessionId,
    messages: stored?.messages ?? [],
    selectedProject: stored?.selectedProject ?? null,
  });
};
